import { useState, useEffect, useMemo, useRef, lazy, Suspense } from "react";
import "@/App.css";
import { LEVELS_META, DIFFICULTIES, getDailyChallenge, CHARACTERS, FEATURED_LEVELS } from "@/game/config";
import TitleScreen from "@/components/screens/TitleScreen";
import CharacterSelect from "@/components/screens/CharacterSelect";
const CharacterCollection = lazy(() => import("@/components/screens/CharacterCollection"));
import LevelSelect from "@/components/screens/LevelSelect";
const Leaderboard = lazy(() => import("@/components/screens/Leaderboard"));
import GameCanvas from "@/components/GameCanvas";
import BonusRound from "@/components/BonusRound";
import HowToPlay from "@/components/HowToPlay";
import ResultOverlay from "@/components/screens/ResultOverlay";
import VictoryCelebration from "@/components/screens/VictoryCelebration";
import BonusPrompt from "@/components/screens/BonusPrompt";
import OceanBonusPrompt from "@/components/screens/OceanBonusPrompt";
import OceanDiveRound from "@/components/OceanDiveRound";
import RodeoRound from "@/components/RodeoRound";
import RodeoLevelScreen from "@/components/RodeoLevelScreen";
const AdminDashboard = lazy(() => import("@/components/screens/AdminDashboard"));
import AuthModal from "@/components/AuthModal";
const TournamentScreen = lazy(() => import("@/components/screens/TournamentScreen"));
import OpenInBrowserBanner from "@/components/OpenInBrowserBanner";
import SuggestionsModal from "@/components/SuggestionsModal";
import InstallPrompt from "@/components/InstallPrompt";
const MediaKit = lazy(() => import("@/components/screens/MediaKit"));
const PartnersPage = lazy(() => import("@/components/screens/PartnersPage"));
const FishingGame = lazy(() => import("@/components/FishingGame"));
import { FishingLoader } from "@/components/fishing/FishingLoader";
import { NotificationPopup } from "@/components/NotificationPopup";
import NewCharsPopup from "@/components/NewCharsPopup";
import PromoWelcome from "@/components/screens/PromoWelcome";
import WelcomeBonusModal from "@/components/WelcomeBonusModal";
import CouponModal from "@/components/CouponModal";
import { giftcardPoints } from "@/lib/giftcard";
import VerifyCouponModal from "@/components/VerifyCouponModal";
import { LangToggle } from "@/components/LangToggle";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";
import { toast } from "sonner";
import { useUnlockToast } from "@/hooks/useUnlockToast";
import { api } from "@/lib/api";
import { decodeChallenge } from "@/lib/challenge";
import { resumeBg, stopTitleAudio } from "@/lib/audioBus";
import { Smartphone } from "lucide-react";

const UNLOCK_KEY = "nicabros_unlocked";
const BONUS_KEY = "nicabros_bonus_unlocked";
const HOWTO_KEY = "nicabros_howto_seen";
const DIFF_KEY = "nicabros_diff";
const PROMO_KEY = "nicabros_promo";

export default function App() {
  // When arriving from a direct-launch link (?special=… or ?c=…) start in a
  // neutral "boot" scene so the Title screen — and its auto-playing menu music —
  // never mounts. Otherwise the menu song would briefly overlap the level song.
  const [scene, setScene] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      return p.get("special") || p.get("c") || p.get("gc") ? "boot" : "title";
    } catch { return "title"; }
  });
  const [collectionReturn, setCollectionReturn] = useState("title");
  const [character, setCharacter] = useState(null);
  const [level, setLevel] = useState(null);
  const [result, setResult] = useState(null);
  const [win, setWin] = useState(false);
  const [runId, setRunId] = useState(0);
  const [cordobas, setCordobas] = useState(0);
  const [mode, setMode] = useState("normal"); // "normal" | "daily"
  const daily = useMemo(() => getDailyChallenge(), []);
  const [showAuth, setShowAuth] = useState(false);
  const [fishGameplay, setFishGameplay] = useState(false); // true while inside the fishing casting scene
  const [authMode, setAuthMode] = useState("login");
  const openAuth = (mode) => { setAuthMode(mode === "register" ? "register" : "login"); setShowAuth(true); };
  const [showSuggest, setShowSuggest] = useState(false);
  const [showInstall, setShowInstall] = useState(false);
  const [newCharsOpen, setNewCharsOpen] = useState(false);
  const [promo, setPromo] = useState(null);
  const [couponData, setCouponData] = useState(null);
  const [couponPending, setCouponPending] = useState(false);
  const [pendingScore, setPendingScore] = useState(null); // gift-card run awaiting registration
  const [challenge, setChallenge] = useState(null); // active Gift Card Challenge (Extreme run) or null
  const [challengeEntry, setChallengeEntry] = useState(null); // {points, rank} after a submitted run
  const [verifyCode, setVerifyCode] = useState(() => { try { return new URLSearchParams(window.location.search).get("verify") || ""; } catch { return ""; } });
  const userRef = useRef(null);
  const [pendingFeatured, setPendingFeatured] = useState(null); // featured level to launch after choosing a character (from a share link)
  // In-app browsers (Facebook, Instagram, WhatsApp, TikTok…) lock orientation to
  // portrait and ignore device rotation, leaving players stuck on the rotate
  // screen. Detect them and force a CSS-landscape stage so the game is playable.
  const [forceLandscape, setForceLandscape] = useState(() => {
    try {
      const ua = navigator.userAgent || "";
      return /FBAN|FBAV|FB_IAB|FBIOS|Instagram|Messenger|WhatsApp|Line\/|Twitter|TikTok|musical_ly|Snapchat|Pinterest|LinkedInApp/i.test(ua);
    } catch { return false; }
  });
  const { user, isAdmin, logout, refreshUser } = useAuth();
  userRef.current = user;
  // A share-link player who registers right after a Gift Card run: submit the run they just played.
  useEffect(() => {
    if (!user || !pendingScore) return;
    const p = pendingScore;
    setPendingScore(null);
    api.giftcardSubmit(p.key, p.payload).then((r) => { setChallengeEntry({ points: r.data.entry.points, rank: r.data.me?.rank }); toast.success(t("etScoreSaved")); }).catch(() => {});
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const { t } = useLang();
  useUnlockToast();

  // Daily coin reward: grant +1 the first check-in each day for signed-in players.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    api.claimDailyCoin().then((r) => {
      if (cancelled || !r?.data?.granted) return;
      const streak = r.data.streak || 1;
      const bonus = r.data.streak_bonus || 0;
      toast.success(t("dailyStreakToast")(streak));
      if (bonus > 0) toast.success(t("streakBonusToast")(bonus));
      refreshUser();
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Prompt players to save the game to their phone (once per device, skip if already installed).
  // Arriving from a featured share link (?special=eltimon): hold the Save-to-phone /
  // Register prompts until the match is over so the player gets straight into the level.
  const deferPromptsRef = useRef((() => { const p = new URLSearchParams(window.location.search); return p.has("special") || p.has("gc") || p.has("verify"); })());
  const showInstallPrompt = () => {
    const dismissed = localStorage.getItem("nicabros_install_seen");
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    if (!dismissed && !standalone) setShowInstall(true);
    else if (!user && !localStorage.getItem("nicabros_register_prompt_seen")) {
      localStorage.setItem("nicabros_register_prompt_seen", "1");
      openAuth("register");
    }
  };
  const releaseDeferredPrompts = () => {
    if (!deferPromptsRef.current) return;
    deferPromptsRef.current = false;
    setTimeout(showInstallPrompt, 500);
  };
  useEffect(() => {
    const dismissed = localStorage.getItem("nicabros_install_seen");
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    const params = new URLSearchParams(window.location.search);
    const isChallenge = params.has("c");
    const isPromo = params.has("promo");
    if (deferPromptsRef.current) return;
    if (!dismissed && !standalone && !isChallenge && !isPromo) {
      // Pop the "save to your phone" prompt promptly — on portrait phones it sits
      // above the rotate gate so players save BEFORE rotating to play.
      const tmo = setTimeout(() => setShowInstall(true), 400);
      return () => clearTimeout(tmo);
    }
  }, []);
  const dismissInstall = () => {
    localStorage.setItem("nicabros_install_seen", "1");
    setShowInstall(false);
    // Right after the Save-to-phone prompt, invite brand-new visitors to register
    // (capture email → save scores + get specials). Once per device, signed-out only.
    try {
      if (!user && !localStorage.getItem("nicabros_register_prompt_seen")) {
        localStorage.setItem("nicabros_register_prompt_seen", "1");
        setTimeout(() => openAuth("register"), 350);
      }
    } catch {}
  };

  // "New Characters" popup is opened only from its home-screen button now — no
  // auto-pop on load (kept the entry clean; only Save-to-phone + Register show).

  // Background music: keep the title-selected song playing across the menu
  // scenes (title/character/level) and hand the audio channel to gameplay/bonus.
  useEffect(() => {
    const playScenes = ["title", "character", "level"];
    const stopScenes = ["playing", "bonus", "oceandive", "rodeo", "rodeolevel"];
    if (playScenes.includes(scene)) resumeBg();
    else if (stopScenes.includes(scene)) stopTitleAudio();
  }, [scene]);

  const markNewCharsSeen = () => { try { localStorage.setItem("nicabros_newchars_ts", String(Date.now())); } catch {} setNewCharsOpen(false); };
  const viewNewChars = () => { markNewCharsSeen(); setMode("normal"); setCordobas(0); setScene("character"); };
  const [diffKey, setDiffKey] = useState(() => {
    const v = localStorage.getItem(DIFF_KEY);
    return DIFFICULTIES[v] ? v : "facil";
  });
  const [unlocked, setUnlocked] = useState(LEVELS_META.length - 1);
  const [bonusUnlocked, setBonusUnlocked] = useState(true);
  const [oceanUnlocked, setOceanUnlocked] = useState(true);
  const [autoBonus, setAutoBonus] = useState(false);
  const [autoOcean, setAutoOcean] = useState(false);
  const [challengeTarget, setChallengeTarget] = useState(null);

  // "Beat my score" deep link (?c=...): drop the player straight into the same
  // level + character configuration to compete against the challenger's score.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const tok = params.get("c");
      if (!tok) return;
      // Strip the param so a refresh/replay doesn't re-trigger the challenge.
      params.delete("c");
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
      const ch = decodeChallenge(tok);
      if (!ch) { setScene("title"); return; }
      const lvl = LEVELS_META[ch.levelIndex];
      const chr = CHARACTERS.find((c) => c.id === ch.charId);
      if (!lvl || !chr) { setScene("title"); return; }
      setMode("normal");
      setCordobas(0);
      setCharacter(chr);
      if (DIFFICULTIES[ch.diffId]) setDiffKey(ch.diffId);
      setChallengeTarget({ score: ch.score, name: ch.name });
      api.trackChallenge({ from_name: ch.name, level_index: ch.levelIndex, char_id: ch.charId, target_score: ch.score });
      setLevel(lvl);
      setResult(null);
      setRunId((n) => n + 1);
      setScene("playing");
      if (ch.score > 0) toast(t("challengeStartToast")(ch.name || "?", ch.score.toLocaleString()));
    } catch { setScene("title"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    localStorage.setItem(UNLOCK_KEY, String(unlocked));
  }, [unlocked]);
  useEffect(() => {
    localStorage.setItem(BONUS_KEY, bonusUnlocked ? "1" : "0");
  }, [bonusUnlocked]);

  // Shareable featured-level link (?special=eltimon): auto-pick El Güegüense and
  // drop the player STRAIGHT into that featured level (skip character select) so
  // the coupon is one tap away from a shared post.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const key = (params.get("special") || "").trim().toLowerCase();
      if (!key) return;
      params.delete("special");
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
      const fl = FEATURED_LEVELS.find((l) => l.shareKey === key || l.cityId === key || String(l.index) === key || (l.name || "").toLowerCase().includes(key));
      if (!fl) { setScene("title"); return; }
      const gg = CHARACTERS.find((c) => c.id === "gueguense") || CHARACTERS[0];
      setMode("normal");
      setCordobas(0);
      setDiffKey("facil");
      setCharacter(gg);
      setLevel(fl);
      setResult(null);
      setRunId((n) => n + 1);
      setScene("playing");
      try { localStorage.setItem("nicabros_played", "1"); } catch {}
    } catch { setScene("title"); }
  }, []);

  // Gift Card Challenge deep link (?gc=eltimon, e.g. from the /partners page): boot
  // straight into the challenge level on its fixed (Extreme) difficulty.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const key = (params.get("gc") || "").trim().toLowerCase();
      if (!key) return;
      params.delete("gc");
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
      api.giftcardChallenges().then((r) => {
        const ch = (r.data.challenges || []).find((c) => c.id === key);
        const lvl = ch && (FEATURED_LEVELS.find((l) => l.index === ch.level_index) || LEVELS_META.find((l) => l.index === ch.level_index));
        if (!ch || !lvl) { setScene("title"); return; }
        const gg = CHARACTERS.find((c) => c.id === "gueguense") || CHARACTERS[0];
        setCharacter(gg);
        startChallenge(lvl, ch);
      }).catch(() => setScene("title"));
    } catch { setScene("title"); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    try {
      const ref = new URLSearchParams(window.location.search).get("ref");
      if (ref) localStorage.setItem("nicabros_ref", ref.trim().toUpperCase());
    } catch {}
  }, []);

  // Shareable "Get this character FREE" ad link (?promo=CODE). Tracks a click,
  // stores the code, and shows a sign-up prompt for the free character.
  useEffect(() => {
    let code = "";
    try {
      const params = new URLSearchParams(window.location.search);
      code = (params.get("promo") || "").trim();
      if (!code) return;
      params.delete("promo");
      const qs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
      localStorage.setItem(PROMO_KEY, code);
    } catch { return; }
    api.getPromo(code)
      .then((r) => setPromo(r.data))
      .catch(() => { try { localStorage.removeItem(PROMO_KEY); } catch {} });
  }, []);

  // Auto-claim the stored promo character once the player is signed in
  // (instant for existing users; right after registration for new ones).
  useEffect(() => {
    if (!user) return;
    let code = "";
    try { code = localStorage.getItem(PROMO_KEY) || ""; } catch {}
    if (!code) return;
    api.claimPromo(code)
      .then((r) => {
        const rew = r?.data?.unlocked_character;
        if (rew?.name) { toast.success(`🎁 ${rew.name} unlocked — enjoy!`); refreshUser(); }
      })
      .catch(() => {})
      .finally(() => { try { localStorage.removeItem(PROMO_KEY); } catch {} setPromo(null); });
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    localStorage.setItem(DIFF_KEY, diffKey);
  }, [diffKey]);

  // Capture EVERY landing on the page, even visitors who never play. Fires once per load.
  useEffect(() => { api.trackVisit(user?.id); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Anonymous/registered play tracking: heartbeat while playing.
  useEffect(() => {
    if (scene !== "playing" && scene !== "bonus" && scene !== "oceandive" && scene !== "rodeo" && scene !== "rodeolevel") return;
    api.heartbeat(user?.id);
    const iv = setInterval(() => api.heartbeat(user?.id), 25000);
    return () => clearInterval(iv);
  }, [scene, user]);

  // Track participation in Level 7 (Corrida de Toros) and the 3 bonus rounds so
  // they show up in the admin engagement funnel / level breakdown. Main levels
  // use indices 0-5 and Corrida uses 7; bonuses use out-of-range 10/11/12 so they
  // never inflate the sequential level funnel.
  useEffect(() => {
    const rounds = {
      rodeolevel: [7, "Corrida de Toros", "corrida"],
      bonus: [10, "Bonus: Volcano Boarding", "bonus"],
      oceandive: [11, "Bonus: Ocean Dive", "bonus"],
      rodeo: [12, "Bonus: Monta de Toros", "bonus"],
    };
    const r = rounds[scene];
    if (!r) return;
    api.logSession({
      user_id: user?.id || null, username: user?.username || "",
      level_index: r[0], level_name: r[1], mode: r[2], duration_ms: 0, completed: false,
    });
  }, [scene]); // eslint-disable-line react-hooks/exhaustive-deps

  const logSession = (r, won) => {
    api.logSession({
      user_id: user?.id || null, username: user?.username || "",
      level_index: level?.index ?? 0, level_name: level?.name || "",
      mode, duration_ms: r?.timeMs || 0, completed: won,
    });
  };

  const startGame = (lvl) => {
    setLevel(lvl);
    setResult(null);
    setChallengeEntry(null);
    setRunId((n) => n + 1);
    setScene("playing");
    try { localStorage.setItem("nicabros_played", "1"); } catch {}
  };
  // Gift Card Challenge: same level, forced to the challenge difficulty (Extreme).
  const startChallenge = (lvl, ch) => { setChallenge(ch); setMode("normal"); setCordobas(0); startGame(lvl); };

  const handleWin = (r) => {
    setResult(r);
    setWin(true);
    logSession(r, true);
    // Gift Card Challenge run (Extreme) → submit the completed run to the challenge board
    // (registered players); anonymous players keep it pending until they register.
    if (challenge) {
      const payload = { score: r?.score || 0, beers: r?.beers || 0, deaths: Math.max(0, (r?.maxLives || 0) - (r?.lives || 0)), time_ms: r?.timeMs || 0, character: character?.name || "", difficulty: challenge.difficulty || "extremo", level_index: level?.index };
      if (userRef.current) api.giftcardSubmit(challenge.id, payload).then((res) => setChallengeEntry({ points: res.data.entry.points, rank: res.data.me?.rank })).catch(() => {});
      else { setPendingScore({ key: challenge.id, payload }); setChallengeEntry({ points: giftcardPoints(challenge, payload), rank: null }); }
    }
    // Featured partner level → issue the player a save-to-phone discount coupon.
    if (level?.coupon) {
      const c = level.coupon;
      setCouponPending(true);
      api.issueCoupon(c.brand, c.percent, c.days).then((res) => setCouponData(res.data)).catch(() => { setCouponPending(false); releaseDeferredPrompts(); });
    } else releaseDeferredPrompts();
    if (mode === "normal" && level.index === unlocked && unlocked < LEVELS_META.length - 1) setUnlocked(level.index + 1);
    // Beating Cerro Negro (index 3) unlocks the Volcano BONUS and offers a Play/Skip
    // choice before the bonus round starts.
    if (mode === "normal" && level.index === 3) {
      setBonusUnlocked(true);
      setCordobas(0);
      setScene("bonusPrompt");
      return;
    }
    // Beating Corn Island (last level) unlocks the Underwater Treasure Dive BONUS
    // and offers a Play/Skip choice before the dive starts.
    if (mode === "normal" && level.index === LEVELS_META.length - 1) {
      setOceanUnlocked(true);
      setCordobas(0);
      setScene("oceanPrompt");
      return;
    }
    setScene("result");
  };

  const handleGameOver = (r) => {
    setResult(r);
    setWin(false);
    logSession(r, false);
    setScene("result");
    releaseDeferredPrompts();
  };
  // From the El Timón end screen, flow straight into Managua (Level 1).
  const playManagua = () => { setCouponData(null); setChallenge(null); setMode("normal"); setCordobas(0); startGame(LEVELS_META[0]); };

  // The Volcano is a standalone BONUS level, unlocked after finishing all 3
  // levels and launched from the level-select screen. Daily mode is single-level.
  const startBonus = () => { setCordobas(0); setScene("bonus"); };
  const startOcean = () => { setCordobas(0); setAutoOcean(false); setScene("oceandive"); };
  const startRodeo = () => { setCordobas(0); setScene("rodeo"); };
  const startRodeoLevel = () => { setCordobas(0); setScene("rodeolevel"); };
  // Show the "How to Play" popup before a level starts. Always shows before
  // Level 1 (index 0); on other levels it shows only to anonymous players.
  // In all cases the player can opt out via the "don't show again" checkbox.
  const chooseLevel = (lvl) => {
    setChallenge(null);
    const optedOut = localStorage.getItem(HOWTO_KEY) === "1";
    if (!optedOut && (lvl.index === 0 || !user?.username)) { setLevel(lvl); setScene("howto"); }
    else startGame(lvl);
  };
  const nextLevel = () => {
    if (mode === "daily") { setScene("title"); return; }
    const next = LEVELS_META[level.index + 1];
    if (next) startGame(next);
  };
  // Replay the SAME level at a harder tier (difficulty progression / more coins).
  const stepUpTier = (tierId) => {
    if (DIFFICULTIES[tierId]) { setDiffKey(tierId); startGame(level); }
  };

  const effDiffId = mode === "daily" ? daily.diffId : (level?.featured ? (challenge ? (challenge.difficulty || "extremo") : "facil") : diffKey);
  const effDiff = DIFFICULTIES[effDiffId] || DIFFICULTIES.normal;
  const showToggle = scene !== "playing" && scene !== "bonus" && scene !== "oceandive" && scene !== "rodeo" && scene !== "rodeolevel";

  // Public, no-auth Sponsor Media Kit page (shareable link).
  if (typeof window !== "undefined" && window.location.pathname.replace(/\/$/, "") === "/reach") {
    return <div className="App app-root bg-[#0c0c14] overflow-hidden relative"><MediaKit /></div>;
  }

  // Public, shareable Brand Partner discounts page (gated by play/login).
  if (typeof window !== "undefined" && window.location.pathname.replace(/\/$/, "") === "/partners") {
    return (
      <div className="App app-root bg-[#0c0c14] overflow-hidden relative">
        <Suspense fallback={<div className="fixed inset-0 flex items-center justify-center bg-[#0c0c14] text-white font-pixel text-sm">…</div>}>
          <PartnersPage />
        </Suspense>
      </div>
    );
  }

  return (
    <div className={`App app-root bg-[#0c0c14] overflow-hidden relative${forceLandscape ? " force-landscape" : ""}`}>
      <Suspense fallback={<div className="fixed inset-0 flex items-center justify-center bg-[#0c0c14] text-white font-pixel text-sm">…</div>}>
      <OpenInBrowserBanner />
      {showToggle && scene !== "title" && scene !== "tournament" && !(scene === "fishing" && fishGameplay) && <LangToggle className={`absolute top-3 z-[80] ${scene === "fishing" ? "right-3 lang-safe-r" : "left-3"}`} />}

      {scene === "boot" && (
        <div className="fixed inset-0 flex items-center justify-center bg-[#0a1c38] text-[#FFD54F] font-pixel text-sm" data-testid="boot-loader">
          El Timón…
        </div>
      )}

      {scene === "title" && <TitleScreen onStart={() => { setMode("normal"); setCordobas(0); setScene("character"); }} onDaily={() => { setMode("daily"); setCordobas(0); setScene("character"); }} onLeaderboard={() => setScene("leaderboard")} onAuth={openAuth} onSuggest={() => setShowSuggest(true)} onAdmin={() => setScene("admin")} onShowNewChars={() => setNewCharsOpen(true)} onTournament={() => setScene("tournament")} onFishing={() => setScene("fishing")} onCollection={() => { setMode("normal"); setCollectionReturn("title"); setScene("collection"); }} daily={daily} />}

      {scene === "title" && <NewCharsPopup open={newCharsOpen} onClose={markNewCharsSeen} onView={viewNewChars} onFishing={() => { markNewCharsSeen(); setScene("fishing"); }} />}

      {scene === "character" && (
        <CharacterSelect mode={mode} daily={daily} onSelect={(c) => { setCharacter(c); if (pendingFeatured) { const fl = pendingFeatured; setPendingFeatured(null); chooseLevel(fl); } else if (mode === "daily") startGame(LEVELS_META[daily.levelIndex]); else setScene("level"); }} onBack={() => setScene("title")} onSuggest={() => setShowSuggest(true)} onCollection={() => { setCollectionReturn("character"); setScene("collection"); }} />
      )}
      {scene === "collection" && <CharacterCollection onBack={() => setScene(collectionReturn)} />}

      {scene === "level" && (
        <LevelSelect character={character} unlocked={isAdmin ? LEVELS_META.length - 1 : unlocked} bonusUnlocked={isAdmin ? true : bonusUnlocked} oceanUnlocked={isAdmin ? true : oceanUnlocked} diffKey={diffKey} onDiff={setDiffKey} onSelect={chooseLevel} onBonus={startBonus} onOceanBonus={startOcean} onRodeoBonus={startRodeo} onRodeoLevel={startRodeoLevel} onFeatured={chooseLevel} onChallenge={startChallenge} onAuth={openAuth} onBack={() => setScene("character")} onSuggest={() => setShowSuggest(true)} />
      )}

      {scene === "howto" && (
        <HowToPlay onPlay={(dontShow) => { if (dontShow) localStorage.setItem(HOWTO_KEY, "1"); startGame(level); }} onAuth={() => setShowAuth(true)} />
      )}

      {scene === "leaderboard" && <Leaderboard onBack={() => setScene("title")} daily={daily} />}
      {scene === "fishing" && (
        <Suspense fallback={<FishingLoader />}>
          <FishingGame onExit={() => setScene("title")} onGameplayChange={setFishGameplay} />
        </Suspense>
      )}
      {scene === "tournament" && <TournamentScreen onBack={() => setScene("title")} onAuth={() => setShowAuth(true)} />}
      {scene === "admin" && <AdminDashboard onBack={() => setScene("title")} />}

      {scene === "playing" && <GameCanvas key={runId} character={character} level={level} difficulty={effDiff} onWin={handleWin} onGameOver={handleGameOver} onExit={() => setScene("title")} onRestart={() => startGame(level)} onSuggest={() => setShowSuggest(true)} />}

      {scene === "bonusPrompt" && (
        <BonusPrompt
          onPlay={() => { setCordobas(0); setAutoBonus(true); setScene("bonus"); }}
          onSkip={() => setScene("result")}
        />
      )}

      {scene === "bonus" && (
        <BonusRound character={character} onContinue={(r) => { setCordobas(r?.collected || 0); if (autoBonus) { setAutoBonus(false); setScene("result"); } else { setScene("level"); } }} onExit={() => setScene("level")} />
      )}

      {scene === "oceanPrompt" && (
        <OceanBonusPrompt
          onPlay={() => { setCordobas(0); setAutoOcean(true); setScene("oceandive"); }}
          onSkip={() => setScene("result")}
        />
      )}

      {scene === "oceandive" && (
        <OceanDiveRound character={character} onContinue={(r) => { setCordobas(r?.collected || 0); if (autoOcean) { setAutoOcean(false); setScene("result"); } else { setScene("level"); } }} onExit={() => setScene("level")} />
      )}

      {scene === "rodeo" && (
        <RodeoRound character={character} onContinue={() => setScene("level")} onExit={() => setScene("level")} />
      )}

      {scene === "rodeolevel" && (
        <RodeoLevelScreen character={character} difficulty={effDiff} onComplete={() => { setCordobas(0); setScene("rodeo"); }} onExit={() => setScene("level")} />
      )}

      {scene === "result" && (
        <div className="relative w-full h-full">
          <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${level.sky[0]}, ${level.ground})` }} />
          {level.coupon && win && couponPending ? null : win && mode === "normal" && (level.index === LEVELS_META.length - 1 || level.index === 2) ? (
            <VictoryCelebration
              character={character}
              result={result}
              level={level}
              cordobas={cordobas}
              difficulty={effDiffId}
              mode={mode}
              dailyDate={daily.date}
              onSuggest={() => { setScene("title"); setShowSuggest(true); }}
              onMenu={() => setScene("title")}
              onNext={nextLevel}
              onStepUp={stepUpTier}
              hasNext={level.index < LEVELS_META.length - 1}
              challenge={challengeTarget}
            />
          ) : (
            <ResultOverlay
              win={win}
              result={result}
              character={character}
              level={level}
              cordobas={cordobas}
              difficulty={effDiffId}
              mode={mode}
              dailyDate={daily.date}
              hasNext={mode === "normal" && level.index < LEVELS_META.length - 1}
              onReplay={() => startGame(level)}
              onNext={nextLevel}
              onStepUp={stepUpTier}
              onMenu={() => setScene("title")}
              onAuth={() => setShowAuth(true)}
              onPlayManagua={level.featured ? playManagua : undefined}
              challenge={challengeTarget}
            />
          )}
        </div>
      )}

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} initialMode={authMode} />}
      {verifyCode && <VerifyCouponModal code={verifyCode} onClose={() => { setVerifyCode(""); try { const p = new URLSearchParams(window.location.search); p.delete("verify"); const qs = p.toString(); window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : "")); } catch {} }} />}
      <WelcomeBonusModal onCollection={() => { setMode("normal"); setCollectionReturn(scene === "character" ? "character" : "title"); setScene("collection"); }} />
      {couponData && <CouponModal coupon={couponData} level={level} score={result?.score || 0} user={user} onAuth={openAuth} challenge={challenge} entry={challengeEntry} run={result} onChallenge={() => { setCouponData(null); api.giftcardChallenges().then((r) => { const ch = (r.data.challenges || []).find((c) => c.level_index === level?.index); if (ch) startChallenge(level, ch); else setScene("level"); }).catch(() => setScene("level")); }} onClose={() => { setCouponData(null); setScene("level"); releaseDeferredPrompts(); }} onReplay={() => { setCouponData(null); startGame(level); }} onPlayManagua={playManagua} />}
      {!user && promo && (
        <PromoWelcome
          promo={promo}
          onSignUp={() => { setPromo(null); setShowAuth(true); }}
          onClose={() => setPromo(null)}
        />
      )}
      {showToggle && <NotificationPopup />}
      {showSuggest && <SuggestionsModal onClose={() => setShowSuggest(false)} onAuth={() => { setShowSuggest(false); setShowAuth(true); }} />}
      {showInstall && !promo && <InstallPrompt onClose={dismissInstall} onSignUp={() => { dismissInstall(); setShowAuth(true); }} />}

      {/* Landscape gate for portrait phones (hidden while a scanned coupon is being verified) */}
      {!verifyCode && <div className="rotate-gate">
        <div className="text-center px-8">
          <Smartphone size={64} strokeWidth={2.5} className="text-[#FFEB3B] mx-auto phone-rotate" />
          <p className="font-pixel text-[#FFEB3B] text-sm pixel-stroke leading-relaxed mt-6">GIRÁ TU<br />TELÉFONO</p>
          <p className="font-vt text-white text-2xl mt-3">Rotate your device to play ▸</p>
          <button
            data-testid="play-anyway-btn"
            onClick={() => setForceLandscape(true)}
            className="mt-7 pixel-btn bg-[#0067C6] text-white px-6 py-3 text-[11px] leading-relaxed"
          >
            ¿No gira? Tocá para jugar<br />Can't rotate? Tap to play ▸
          </button>
        </div>
      </div>}
      </Suspense>
    </div>
  );
}
